export interface YouTubePlayerVars {
  vq?: string;           // Video quality hint
  hl?: string;           // Interface language
  cc_lang_pref?: string; // Captions language preference
  modestbranding?: 0 | 1; // YouTube branding
  rel?: 0 | 1;           // Show related videos
  fs?: 0 | 1;            // Allow fullscreen
  playsinline?: 0 | 1;   // Play inline on mobile
  autoplay?: 0 | 1;      // Autoplay video
  mute?: 0 | 1;          // Start muted
  loop?: 0 | 1;          // Loop video
  controls?: 0 | 1;      // Show YouTube controls
  disablekb?: 0 | 1;     // Disable keyboard controls
  iv_load_policy?: 1 | 3; // Show video annotations
}

export interface VideoQualityConfig {
  youtube: {
    playerVars: YouTubePlayerVars;
  };
  vimeo?: {
    playerOptions: Record<string, any>;
  };
}

/**
 * Generate ReactPlayer config for YouTube videos with quality settings
 */
export const getYouTubeConfig = (quality: string = 'auto'): VideoQualityConfig => {
  const playerVars: YouTubePlayerVars = {
    // Quality hint (YouTube may override based on conditions)
    vq: quality === 'auto' ? undefined : quality,
    
    // Localization
    hl: 'ar',              // Arabic interface
    cc_lang_pref: 'ar',    // Arabic captions preference
    
    // Player behavior
    modestbranding: 1,     // Remove YouTube branding
    rel: 0,                // Don't show related videos at end
    fs: 1,                 // Allow fullscreen
    playsinline: 1,        // Play inline on mobile
    autoplay: 0,           // Don't autoplay
    controls: 0,           // Hide YouTube controls (we use custom)
    disablekb: 1,          // Disable keyboard shortcuts
    iv_load_policy: 3,     // Hide video annotations
  };

  return {
    youtube: {
      playerVars
    }
  };
};

/**
 * Check if a URL is a YouTube video
 */
export const isYouTubeUrl = (url: string): boolean => {
  if (!url) return false;
  
  const youtubePatterns = [
    /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
    /^https?:\/\/(www\.)?youtube\.com\/embed\/[\w-]+/,
    /^https?:\/\/youtu\.be\/[\w-]+/,
    /^https?:\/\/(www\.)?youtube\.com\/v\/[\w-]+/
  ];
  
  return youtubePatterns.some(pattern => pattern.test(url));
};

/**
 * Check if a URL is a Vimeo video
 */
export const isVimeoUrl = (url: string): boolean => {
  if (!url) return false;
  
  const vimeoPatterns = [
    /^https?:\/\/(www\.)?vimeo\.com\/\d+/,
    /^https?:\/\/player\.vimeo\.com\/video\/\d+/
  ];
  
  return vimeoPatterns.some(pattern => pattern.test(url));
};

/**
 * Check if a URL is a Zoom meeting / webinar link (live session — open in Zoom app or browser)
 */
export const isZoomUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  try {
    const hostname = new URL(trimmed).hostname.toLowerCase();
    return (
      hostname === 'zoom.us' ||
      hostname.endsWith('.zoom.us') ||
      hostname === 'zoom.com.cn' ||
      hostname.endsWith('.zoom.com.cn') ||
      hostname.includes('zoomgov.com')
    );
  } catch {
    return /\bzoom\.(us|com\.cn)\b/i.test(trimmed) || /zoomgov\.com/i.test(trimmed);
  }
};

/**
 * Check if a URL is a Bunny.net video
 */
export const isBunnyVideo = (url: string): boolean => {
  return url.includes('iframe.mediadelivery.net');
};

/**
 * Get video platform type from URL
 */
export const getVideoPlatform = (url: string): 'youtube' | 'vimeo' | 'bunny' | 'other' => {
  if (isYouTubeUrl(url)) return 'youtube';
  if (isVimeoUrl(url)) return 'vimeo';
  if (isBunnyVideo(url)) return 'bunny';
  return 'other';
};

/**
 * Quality level definitions
 */
export const QUALITY_LEVELS = {
  auto: { label: 'Auto', resolution: 'Adaptive', bitrate: 'Smart' },
  hd1080: { label: '1080p', resolution: '1920×1080', bitrate: 'High' },
  hd720: { label: '720p', resolution: '1280×720', bitrate: 'High' },
  medium: { label: '480p', resolution: '854×480', bitrate: 'Medium' },
  small: { label: '360p', resolution: '640×360', bitrate: 'Low' },
  tiny: { label: '240p', resolution: '426×240', bitrate: 'Very Low' }
} as const;

/**
 * Get quality label from value
 */
export const getQualityLabel = (quality: string): string => {
  return QUALITY_LEVELS[quality as keyof typeof QUALITY_LEVELS]?.label || 'Auto';
};

/**
 * Get recommended quality based on connection speed
 */
export const getRecommendedQuality = (connectionSpeed: 'fast' | 'medium' | 'slow'): string => {
  switch (connectionSpeed) {
    case 'fast':
      return 'hd1080';
    case 'medium':
      return 'hd720';
    case 'slow':
      return 'small';
    default:
      return 'auto';
  }
};

/**
 * Validate quality value
 */
export const isValidQuality = (quality: string): boolean => {
  return Object.keys(QUALITY_LEVELS).includes(quality);
};

/**
 * Get quality options for a specific platform
 */
export const getQualityOptions = (platform: ReturnType<typeof getVideoPlatform>) => {
  const allOptions = Object.entries(QUALITY_LEVELS).map(([value, config]) => ({
    value,
    ...config
  }));

  // For YouTube, all options are available but controlled by YouTube
  if (platform === 'youtube') {
    return allOptions;
  }

  // For other platforms, we might have more control
  return allOptions;
};

/**
 * Format quality change notification message
 */
export const getQualityChangeMessage = (
  newQuality: string, 
  platform: ReturnType<typeof getVideoPlatform>
): string => {
  const qualityLabel = getQualityLabel(newQuality);
  
  if (platform === 'youtube') {
    return `Quality preference set to ${qualityLabel}. YouTube will adjust based on your connection.`;
  }
  
  return `Video quality changed to ${qualityLabel}.`;
};



